import { HttpClient, HttpContext } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { ApiResponse } from "src/app/core/models/api.model";
import { SKIP_SESSION_LOGOUT_ON_401 } from "../interceptors/auth.interceptor";
import { PersonaPromptsQuota } from "src/app/core/models/persona.model";

/** `GET v2/user-persona/quota`'s node replacement — the PERSONA-COUNT quota
 * the "create new persona" flow gates on. Deliberately NOT the AI-prompt
 * quota (`PersonaPromptsQuota`) despite the similarly-named URL — see
 * `PersonaAiApiService.getPersonaQuota()`'s own doc comment. `created` is
 * optional here only because the new-persona quota GATE (`user-persona-add
 * .component.ts`) never reads it — the persona LIST page does, and always
 * receives it from the real endpoint. */
export interface PersonaCountQuota {
  created?: number;
  quota: number;
  remaining: number;
}

/** `GET v2/meta-data/quota`'s node replacement — the narrow slice the
 * persona editor's quota block/refill-countdown/regeneration gate reads.
 * Mirrors `PersonaQuotaMeta`'s wire shape (snake_case, as the backend
 * sends it) rather than the camelCase client-side model. */
export interface PersonaAiQuotaMetaResponse {
  quota: PersonaPromptsQuota;
  show_upgrade_button?: boolean;
  is_enterprise?: boolean;
  persona_regeneration?: {
    allowed: boolean;
    free_generations: number;
    limit?: number;
  };
}

/** A single `prompt_tone` row, returned byte-for-byte as legacy stored it —
 * every consumer here only ever reads `id`/`name`. */
export interface PersonaPromptToneRow {
  id: string;
  name: string;
  [key: string]: unknown;
}

export interface PersonaPromptTonesResponse {
  data: PersonaPromptToneRow[];
}

/** `POST v2/user-persona/generate-persona`'s node replacement's response —
 * `data` is the generated text; `quota` is the FLAT, already-`used`-
 * reflecting prompt quota (never the `{quota, show_upgrade_button, ...}`
 * envelope `getPromptQuota()` returns). */
export interface PersonaGenerateResult {
  data: string;
  quota_consumed?: boolean;
  quota?: PersonaPromptsQuota;
}

/**
 * `generatePersona()`'s response type also tolerates a bare STRING at
 * `result` — never node's real shape (always the object above), but a
 * defensive carryover from the retired Laravel contract, which
 * `user-persona-add.component.ts`'s own tolerant parsing still handles.
 */
export type PersonaGenerateResponseResult = PersonaGenerateResult | string;

/**
 * API client for flymsg-node's persona-generation surface (issue #582,
 * AI-27) — `POST /api/v3/user-persona/generate-persona`,
 * `GET /api/v3/meta-data/quota` (the daily AI-prompt quota — NOT
 * `user-persona/quota`, see below), `GET /api/v3/user-persona/quota` (the
 * persona-COUNT quota), `GET /api/v3/prompt-tones`. Persona CRUD
 * (list/create/update/set-default/delete) stays on Laravel via
 * `DataService` — out of scope for this cutover, see flymsg-node's
 * `userPersonaAiController.ts`.
 *
 * Built on plain `HttpClient` against `environment.NODE_API_URL`, mirroring
 * `NotificationApiService`/`flymsg-admin-fe`'s `NodeApiService`: `DataService`
 * is hardwired to the Laravel `environment.API_URL` and its error-handling
 * machinery (plan-limit dialogs, `DEACTIVATED` flow) doesn't apply to this
 * node-hosted surface. The global `AuthInterceptor` still attaches the
 * bearer token regardless of base URL.
 *
 * Every request here carries {@link SKIP_SESSION_LOGOUT_ON_401}: node
 * validates the bearer independently of Laravel and can reject a token
 * Laravel still accepts (e.g. mid-rollout, or a route not yet wired), so a
 * 401 here must degrade the calling feature, never eject the whole FlyMSG
 * session — mirrors flymsg-admin-fe#103 (AI-34) and flymsg-cmc-fe/
 * flymsg-roleplay's identical pattern.
 *
 * **Every caller must read the server's error message, not just its
 * `code`.** A 4xx here carries `{code, error, message, ...}` (a
 * `RawBodyError` — node bypasses the `{result:...}` envelope for any body
 * with a top-level `error` key, so `HttpErrorResponse.error` IS this object
 * directly, no `.error.result` unwrap needed) for the quota/plan-gate
 * refusals this feature cares about; a 422 validation failure carries
 * `{error: <first message>, error_list: {...}}` in the same unwrapped
 * shape. See `user-persona-add.component.ts#handleGenerateError` for the
 * full precedence chain.
 */
@Injectable({ providedIn: "root" })
export class PersonaAiApiService {
  constructor(private readonly http: HttpClient) {}

  private context(): HttpContext {
    return new HttpContext().set(SKIP_SESSION_LOGOUT_ON_401, true);
  }

  private url(path: string): string {
    return `${environment.NODE_API_URL}/api/v3/${path}`;
  }

  /**
   * `POST /api/v3/user-persona/generate-persona`. `payload` is the raw
   * validated persona-generation body (`name`, `industry`,
   * `prompt_tone_id`, `type`, the profile array/string fields, `regenerate`,
   * `user_persona_id`, ...) — the same shape the component already builds
   * for the retired `v2/user-persona/generate-persona` Laravel call.
   */
  generatePersona(payload: Record<string, unknown>): Observable<ApiResponse<PersonaGenerateResponseResult>> {
    return this.http.post<ApiResponse<PersonaGenerateResult>>(this.url("user-persona/generate-persona"), payload, {
      context: this.context(),
    });
  }

  /**
   * `GET /api/v3/meta-data/quota` — the DAILY AI-PROMPT quota block
   * (`quota`, `show_upgrade_button`, `is_enterprise`, `persona_regeneration`)
   * the persona editor's `updatePrompts()`/`applyQuotaPayload()` read.
   * NOT the persona-count quota — see {@link getPersonaQuota}.
   */
  getPromptQuota(): Observable<ApiResponse<PersonaAiQuotaMetaResponse>> {
    return this.http.get<ApiResponse<PersonaAiQuotaMetaResponse>>(this.url("meta-data/quota"), {
      context: this.context(),
    });
  }

  /**
   * `GET /api/v3/user-persona/quota` — the PERSONA-COUNT quota the
   * "create new persona"/duplicate flow gates on (`created`, `quota`,
   * `remaining`). A DIFFERENT counter from {@link getPromptQuota}'s daily
   * AI-prompt quota, despite the near-identical URL — read each from the
   * right place (see flymsg-node's `userPersonaAiController.ts` doc
   * comment, verified against the real `UserPersonaController::getQuota()`
   * source).
   */
  getPersonaQuota(): Observable<ApiResponse<PersonaCountQuota>> {
    return this.http.get<ApiResponse<PersonaCountQuota>>(this.url("user-persona/quota"), {
      context: this.context(),
    });
  }

  /**
   * `GET /api/v3/prompt-tones` — the tone-of-voice lookup list. Node
   * returns `{result:{data:[...]}}` for every FlyAI lookup; callers must
   * guard with `?? []` before `.map`-ing (a non-array response must never
   * blank the tone dropdown).
   */
  getTones(): Observable<ApiResponse<PersonaPromptTonesResponse>> {
    return this.http.get<ApiResponse<PersonaPromptTonesResponse>>(this.url("prompt-tones"), {
      context: this.context(),
    });
  }
}
